import {useEffect, useState} from "react";
import { useDriverContextDispatch } from '../context/DriverContext.js';
import { useNavigate } from 'react-router-dom';
import ComfyGallery from '../components/ComfyGallery';
import ComfyOutputLayout from '../components/ComfyOutputLayout';
import { rebuildOutputDatabase, regenerateAllVideoThumbnails } from '../services/comfyOutputService';
import electronBridge from '../utils/electronBridge';

export const defaultPrompt = {
    accountNo: "",
    error: "",
    progress: 0,
    prompt: {
        prompt: "",
        negative_prompt: "",
        enhanced: false,
        prompt_output: "text-image",
        audio_prompt: "",
        input_language: "en",
        meme_prompt: false,
        params: {
            seed_value: null,
            num_inference_steps: 12,
            guidance_scale: 7.5,
            scheduler: "DPM-Solver",
            model: "stable-diffusion-v1-5",
            lora: "None",
            lora_scale: 0,
            width: 512,
            height: 512,
            count: 1,
            symmetric: false,
            shuffle: false,
            safety_checker: true,
            input_image: null,
            tile: false,
            upscale: false,
            upscale_factor: 0,
            control_net: "",
            mask_image: null,
            full_precision: false,
            half_model: false,
            cpu_generation: false,
            cpu_offloading: false,
            image_batch_size: 1
        }
    }
};

/**
 * ComfyOutput Container
 * Displays the output of ComfyUI generation including images, videos, and audio files
 * 
 * This container manages the state and logic, while the layout is handled by ComfyOutputLayout
 */
export default function ComfyOutput() {
    const [category, setCategory] = useState("all");
    const [sortMethod, setSortMethod] = useState("newest");
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [totalStoredItems, setTotalStoredItems] = useState(0);
    const [isRebuildingDatabase, setIsRebuildingDatabase] = useState(false);
    const [columnClass, setColumnClass] = useState("cols-4");
    
    const driverContextDispatch = useDriverContextDispatch();
    const navigate = useNavigate();
    const ipcRenderer = electronBridge.ipcRenderer;

    useEffect(() => {
        ipcRenderer.send("pwa-log", "React - ComfyOutput Page");
        
        // Set the default header for ComfyOutput page
        driverContextDispatch({
            type: 'setupHeader',
            payload: {
                header: {
                    title: "ComfyUI Output",
                    hideBackButton: true,
                    hideSearchButton: true,
                    hideSettingsButton: false
                }
            }
        });
    
        return () => {
            // Cleanup
        };
    }, []);

    // Handle category change
    const handleCategoryChange = (newCategory) => {
        setCategory(newCategory);
    };

    // Handle sort method change
    const handleSortMethodChange = (newSortMethod) => {
        setSortMethod(newSortMethod);
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    // Update total counts when ComfyGallery reports them
    const handleCountUpdate = (counts) => {
        console.log("ComfyOutput received count update:", counts);
        if (counts) {
            // Only update if we have valid counts and they're different from current state
            const newVisibleCount = counts.totalItems || 0;
            const newTotalCount = counts.totalStoredItems || 0;
            
            if (newVisibleCount !== totalItems || newTotalCount !== totalStoredItems) {
                setTotalItems(newVisibleCount);
                setTotalStoredItems(newTotalCount);
                console.log("Updated state with totalItems:", newVisibleCount, "totalStoredItems:", newTotalCount);
            } else {
                console.log("Count values unchanged, skipping update");
            }
        } else {
            console.warn("Received empty counts object");
        }
    };

    // Handle rebuilding the database
    const handleRebuildDatabase = async () => {
        if (isRebuildingDatabase) return;
        
        setIsRebuildingDatabase(true);
        setLoading(true);
        
        try {
            const result = await rebuildOutputDatabase();
            // ComfyGallery will automatically refresh
            console.log("Database rebuild result:", result);
        } catch (error) {
            console.error('Error rebuilding database:', error);
        } finally {
            setIsRebuildingDatabase(false);
            setLoading(false);
        }
    };

    // Handle regenerating video thumbnails
    const handleRegenerateVideoThumbnails = async () => {
        if (isRebuildingDatabase) return;
        
        setIsRebuildingDatabase(true);
        setLoading(true);
        
        try {
            const result = await regenerateAllVideoThumbnails();
            console.log("Video thumbnail regeneration result:", result);
        } catch (error) {
            console.error('Error regenerating video thumbnails:', error);
        } finally {
            setIsRebuildingDatabase(false);
            setLoading(false);
        }
    };

    // Handle column class change
    const handleColumnClassChange = (newColumnClass) => {
        setColumnClass(newColumnClass);
    };

    // Convert column class to column count
    const getColumnCount = () => {
        switch (columnClass) {
            case "cols-2": return 2;
            case "cols-3": return 3;
            case "cols-5": return 5;
            case "cols-6": return 6;
            case "cols-4":
            default: return 4;
        }
    };

    return (
        <ComfyOutputLayout
            category={category}
            sortMethod={sortMethod}
            onCategoryChange={handleCategoryChange}
            onSortMethodChange={handleSortMethodChange}
            loading={loading}
            onRebuildDatabase={handleRebuildDatabase}
            onRegenerateVideoThumbnails={handleRegenerateVideoThumbnails}
            page={page}
            totalItems={totalItems}
            totalStoredItems={totalStoredItems}
            onPageChange={handlePageChange}
            columnClass={columnClass}
            onColumnClassChange={handleColumnClassChange}
        >
            <ComfyGallery 
                category={category} 
                sortMethod={sortMethod} 
                onCategoryChange={handleCategoryChange}
                columnCount={getColumnCount()}
                onCountUpdate={handleCountUpdate}
            />
        </ComfyOutputLayout>
    );
}